import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ledgerPlatformAPI } from '@/services';

const LEDGER_APP_URL = process.env.REACT_APP_LEDGER_URL || 'http://localhost:3004';

/** Synthèse Ledgera (plateforme) — complète les stats Entrea locales sans les confondre */
export const LedgerPlatformWidget = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const [balance, setBalance] = useState(null);
  const [volume, setVolume] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [b, s] = await Promise.all([
          ledgerPlatformAPI.getOrgBalance(),
          ledgerPlatformAPI.getStatsSummary(),
        ]);
        if (cancelled) return;
        setBalance(b.data?.current_balance ?? null);
        const vol = s.data?.volume;
        setVolume(vol ? { total: vol.total_amount, count: vol.count } : null);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr('ledger');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white rounded-lg border border-teal-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">{t('dashboardWidgets.ledgerTitle')}</h3>
        <a
          href={LEDGER_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-teal-700 hover:text-teal-900"
        >
          {t('dashboardWidgets.ledgerOpenApp')}
        </a>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {t('dashboardWidgets.ledgerBlurb')}
      </p>
      {err && (
        <p className="text-sm text-amber-700">
          {t('dashboardWidgets.ledgerUnavailable')}
        </p>
      )}
      {!err && balance !== null && (
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-gray-500">{t('dashboardWidgets.ledgerOrgBalance')}</span>
            <span className="text-xl font-semibold text-teal-800">
              {Number(balance).toLocaleString(localeFmt)} <span className="text-sm text-gray-500">FBU</span>
            </span>
          </div>
          {volume && (
            <div className="flex justify-between items-baseline text-sm">
              <span className="text-gray-500">{t('dashboardWidgets.ledgerPostedVolume')}</span>
              <span>
                {t('dashboardWidgets.ledgerVolumeLines', {
                  amount: Number(volume.total || 0).toLocaleString(localeFmt),
                  count: volume.count || 0,
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Widget pour Trésorier - Focus Financier
export const TreasurerFinancialWidget = ({ stats }) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  if (!stats) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-semibold text-gray-900">{t('dashboardWidgets.treasurerTitle')}</h3>
        <Link
          to="/my-fund/financial"
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {t('dashboardWidgets.seeAll')}
        </Link>
      </div>
      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.balance')}</span>
          <span className="text-xl font-semibold text-gray-900">
            {stats.total_balance?.toLocaleString(localeFmt) || 0} <span className="text-sm text-gray-500">FBU</span>
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.thisMonth')}</span>
          <span className="text-lg font-medium text-gray-700">
            {stats.contributions_current_month?.toLocaleString(localeFmt) || 0} <span className="text-sm text-gray-500">FBU</span>
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.pending')}</span>
          <span className="text-base font-medium text-orange-600">
            {stats.unpaid_contributions_count || 0}
          </span>
        </div>
        <div className="pt-3 border-t border-gray-100">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-gray-500">{t('dashboardWidgets.paymentRate')}</span>
            <span className="text-base font-semibold text-gray-900">
              {stats.payment_rate ? `${stats.payment_rate}%` : t('home.notApplicable')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Widget pour Secrétaire - Focus Membres et Réunions
export const SecretaryWidget = ({ stats }) => {
  const { t } = useTranslation('common');
  if (!stats) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-6">{t('dashboardWidgets.overviewTitle')}</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.members')}</span>
          <div className="text-right">
            <span className="text-xl font-semibold text-gray-900">{stats.total_members || 0}</span>
            <span className="text-xs text-gray-500 ml-2">
              {t('dashboardWidgets.activeCountParen', { count: stats.active_members || 0 })}
            </span>
          </div>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.meetingsToPublish')}</span>
          <span className="text-lg font-medium text-orange-600">
            {stats.pending_meetings_count || 0}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.upcomingMeetingsShort')}</span>
          <span className="text-base font-medium text-gray-700">
            {stats.upcoming_meetings || 0}
          </span>
        </div>
        <div className="pt-3 border-t border-gray-100">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-gray-500">{t('dashboardWidgets.membersLate')}</span>
            <span className="text-base font-semibold text-red-600">
              {stats.members_with_unpaid || 0}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6 flex gap-2">
        <Link
          to="/members"
          className="flex-1 px-3 py-2 text-center text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('nav.members')}
        </Link>
        <Link
          to="/calendar"
          className="flex-1 px-3 py-2 text-center text-sm text-white bg-gray-900 rounded-lg hover:bg-gray-800"
        >
          {t('nav.calendar')}
        </Link>
      </div>
    </div>
  );
};

// Widget pour Président - Vue d'ensemble avec alertes
export const PresidentOverviewWidget = ({ stats }) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  if (!stats) return null;

  const hasAlerts = (stats.pending_transactions_count || 0) > 0 || 
                    (stats.unpaid_contributions_count || 0) > 0 || 
                    (stats.pending_meetings_count || 0) > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-6">{t('dashboardWidgets.overviewTitle')}</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.balance')}</span>
          <span className="text-xl font-semibold text-gray-900">
            {stats.total_balance?.toLocaleString(localeFmt) || 0} <span className="text-sm text-gray-500">FBU</span>
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.presidentActiveMembers')}</span>
          <span className="text-base font-medium text-gray-700">
            {stats.active_members || 0} / {stats.total_members || 0}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.presidentRecoveryRate')}</span>
          <span className="text-base font-semibold text-gray-900">
            {stats.contribution_rate ? `${stats.contribution_rate}%` : t('home.notApplicable')}
          </span>
        </div>
        {hasAlerts && (
          <div className="pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase mb-3">{t('dashboardWidgets.actionsRequired')}</p>
            <div className="space-y-2 text-sm">
              {stats.pending_transactions_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboardWidgets.alertTransactions')}</span>
                  <span className="font-medium text-red-600">{stats.pending_transactions_count}</span>
                </div>
              )}
              {stats.unpaid_contributions_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboardWidgets.alertContributions')}</span>
                  <span className="font-medium text-orange-600">{stats.unpaid_contributions_count}</span>
                </div>
              )}
              {stats.pending_meetings_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboardWidgets.alertMeetings')}</span>
                  <span className="font-medium text-amber-600">{stats.pending_meetings_count}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Widget de tendances avec indicateurs
export const TrendsWidget = ({ stats }) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  if (!stats) return null;

  const trends = useMemo(
    () =>
      [
        {
          label: t('dashboardWidgets.trendBalanceTotal'),
          value: stats.balance_change_percentage,
          prefix: stats.total_balance?.toLocaleString(localeFmt) || 0,
          suffix: 'FBU',
          color: 'indigo',
        },
        {
          label: t('dashboardWidgets.trendContributionsMonth'),
          value: stats.contributions_change_percentage,
          prefix: stats.contributions_current_month?.toLocaleString(localeFmt) || 0,
          suffix: 'FBU',
          color: 'green',
        },
        {
          label: t('dashboardWidgets.trendExpensesMonth'),
          value: stats.expenses_change_percentage,
          prefix: stats.total_expenses?.toLocaleString(localeFmt) || 0,
          suffix: 'FBU',
          color: 'red',
        },
      ].filter((row) => row.value !== null && row.value !== undefined),
    [stats, t, localeFmt],
  );

  const colorClasses = {
    indigo: { bg: 'bg-entrea-panel', text: 'text-entrea-royal' },
    green: { bg: 'bg-green-500', text: 'text-green-600' },
    red: { bg: 'bg-red-500', text: 'text-red-600' }
  };

  if (trends.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboardWidgets.trendsTitleVsPrev')}</h3>
        <p className="text-sm text-gray-500 text-center py-4">{t('dashboardWidgets.trendsNoData')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-6">{t('dashboardWidgets.trendsTitle')}</h3>
      <div className="space-y-5">
        {trends.map((trend, index) => {
          const colors = colorClasses[trend.color] || colorClasses.indigo;
          const isPositive = trend.value >= 0;
          
          return (
            <div key={index}>
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-sm text-gray-500">{trend.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${colors.text}`}>
                    {isPositive ? '↑' : '↓'} {Math.abs(trend.value).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${colors.bg}`}
                  style={{ width: `${Math.min(Math.abs(trend.value), 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Widget de statistiques de cotisations détaillées
export const ContributionsStatsWidget = ({ stats }) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  if (!stats) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-6">{t('dashboardWidgets.contributionsTitle')}</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.paid')}</span>
          <div className="text-right">
            <span className="text-lg font-semibold text-gray-900">{stats.contributions_paid_count || 0}</span>
            <span className="text-xs text-gray-500 ml-2">
              {stats.contributions_paid_total?.toLocaleString(localeFmt) || 0} FBU
            </span>
          </div>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-gray-500">{t('dashboardWidgets.pending')}</span>
          <div className="text-right">
            <span className="text-lg font-semibold text-amber-600">{stats.contributions_pending_count || 0}</span>
            <span className="text-xs text-gray-500 ml-2">
              {stats.contributions_pending_total?.toLocaleString(localeFmt) || 0} FBU
            </span>
          </div>
        </div>
        <div className="pt-3 border-t border-gray-100">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-gray-500">{t('dashboardWidgets.paymentRate')}</span>
            <span className="text-base font-semibold text-gray-900">
              {stats.payment_rate ? `${stats.payment_rate}%` : t('home.notApplicable')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
